import { AuthContext } from 'contexts';
import { useContext, useEffect, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AnswersModality, Direction, ModelMetadata, Role, UpdateForm } from 'types';
import { fetchWithAuth } from 'utils';
import { apiUrl } from 'utils/constants';

import { Button } from 'components/shadcn/button';
import { Input } from 'components/shadcn/input';
import { Label } from 'components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/shadcn/select';
import { useChatbotList } from 'hooks/useChatbotList';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const DEFAULT_COST_LIMIT = 100;

///////////////////////////////////////////////////////////////////////
// NOTE - this page is bad. our future flow is not suppose to use it //
// it should only be visible to admins                               //
///////////////////////////////////////////////////////////////////////

export function CreateChatForm() {
  const { t } = useTranslation();
  const [modelOptions, setModelOptions] = useState<ModelMetadata[]>([]);
  const { refetch } = useChatbotList();
  const navigate = useNavigate();
  const { role, canEditDynamicPrompts } = useContext(AuthContext);
  const isAdmin = role === Role.ADMIN;

  const methods = useForm({
    defaultValues: {
      costLimit: DEFAULT_COST_LIMIT,
      assistantEmail: '',
      answersModality: AnswersModality.TEXT,
      name: '',
      systemPrompt: t('dashboard.defaults.systemPrompt'),
      dynamicSystemPrompts: { create: [] } as never,
      whatsappPrompt: '',
      firstMessage: t('dashboard.defaults.firstMessage'),
      modelId: 0,
      canAcceptVoiceMessages: false,
      showCredit: true,
      canAcceptMediaMessages: false,
      direction: Direction.RTL,
      textFieldLabel: '',
      openaiKey: '',
      googleKey: '',
      twilioAccountSid: '',
      twilioAuthToken: '',
      twilioMessagingServiceSid: '',
      sendgridApiKey: '',
      airtableLogsBase: '',
      customUserText: '',
      customUserBackground: '',
      temperature: 0.2,
      messagesInMemory: 40,
      showSources: false,
      isSuggestionsEnabled: false,
      isCarouselEnabled: false,
      isRefreshPossible: true,
      functions: [],
      ttsPrompt: '',
      promptVariables: {} as never,
      dataToExtract: {} as never,
      metadataFields: {} as never,
    },
  });

  useEffect(() => {
    // Fetch available models for the dropdown
    const fetchModels = async () => {
      try {
        const response = await fetchWithAuth(`${apiUrl}/model-metadata`);
        if (response.ok) {
          const models = await response.json();
          setModelOptions(models.filter((model: ModelMetadata) => !model.isArchived));
        }
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };

    if (role === Role.ADMIN) {
      fetchModels();
    }
  }, []);

  const formPropsHandler = (data: UpdateForm) => {
    data.functions?.forEach((f) => {
      f.parameters = f.parameters ? JSON.parse(f.parameters as string) : {};
    });
    data.temperature = Number(data.temperature);
    data.messagesInMemory = Number(data.messagesInMemory);
    data.costLimit = Number(data.costLimit);
    data.showSources =
      typeof data.showSources === 'string' ? JSON.parse(data.showSources) : data.showSources;
  };

  const onSubmit: SubmitHandler<UpdateForm> = async (data) => {
    formPropsHandler(data);
    // TODO: we should use shadcn form with zod error handling
    if (!data.name || data.name.trim() === '') {
      methods.setError('name', {
        type: 'manual',
        message: t('dashboard.chatForm.validation.nameRequired'),
      });
      return;
    }
    try {
      delete data.functions;
      const response = await fetchWithAuth(`${apiUrl}/chat-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      // TODO: we might want to check if this was successful
      const newChat = await response.json();
      refetch();
      navigate(`/${newChat.id}/edit`);

      toast.success(t('dashboard.toasts.saved'));
    } catch (error) {
      console.error('Error Submitting data:', error);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <div className={isAdmin ? 'col-span-1' : 'col-span-2'}>
          <div className="space-y-2">
            <Label variant="slate" htmlFor="name">
              {t('dashboard.chatForm.fields.name.label')}
            </Label>
            <Controller
              name="name"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <div className="space-y-1">
                  <Input
                    id="name"
                    {...field}
                    className="w-full"
                    placeholder={t('dashboard.chatForm.fields.name.placeholder')}
                  />
                  {error && <p className="text-sm text-destructive">{error.message}</p>}
                </div>
              )}
            />
          </div>
        </div>

        <div className="col-span-1">
          <div className="space-y-2">
            <Label variant="slate" htmlFor="modelId">
              {t('dashboard.chatForm.fields.model.label')}
            </Label>
            <Controller
              name="modelId"
              control={methods.control}
              render={({ field }) => (
                <Select
                  value={field.value.toString()}
                  onValueChange={(value) => field.onChange(Number(value))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('dashboard.chatForm.fields.model.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map((model) => (
                      <SelectItem key={model.id} value={model.id.toString()}>
                        {model.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>
        <Button
          type="submit"
          variant="secondary"
          className="w-[300px] center bg-gradient-to-r from-primary to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-md hover:shadow-lg transition-all duration-300 transform font-medium rounded-lg"
        >
          {t('dashboard.chatForm.actions.createBot')}
        </Button>
      </form>
    </FormProvider>
  );
}
